import { Component, Inject, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Moment } from 'moment';
import moment from 'moment';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, Observable, combineLatest, filter, finalize, first, firstValueFrom, map, startWith, switchMap, tap } from 'rxjs';
import { AbsenceModalComponent } from 'src/app/absences/absence-form/absence-form.component';
import { Messages } from 'src/app/common/enums';
import { DictionaryService } from 'src/app/data/dictionary.service';
import { EDocumentationService } from 'src/app/data/e-documentation-service';
import { DictionaryItem } from 'src/app/models/DictionaryItem';
import { SnackBarService } from 'src/app/shared/services/snack-bar.service';
import { getBase64 } from 'src/app/common/utils/getBase64';
import { EFolderGridItemDto } from 'src/app/models/dtos/e-folder-grid-item-dto';
import { SubSink } from 'SubSink'
import { TranslateService } from '@ngx-translate/core';
import { WorkerService } from 'src/app/data/worker.service';
import { GetExistingExternalWorkerRequest } from 'src/app/contracts/requests/get-existing-external-worker-request';
import { peselValidator } from 'src/app/workers/worker-form/pesel.validator';
import { SignModalComponent } from '../sign-modal/sign-modal.component';

export interface AddEDocumentFormData {
  dto?: EFolderGridItemDto,
  statusId: number,
  isReadOnly?: boolean
}

@Component({
  selector: 'app-add-e-document-form',
  templateUrl: './add-e-document-form.component.html',
  styleUrls: ['./add-e-document-form.component.scss'],
  standalone: false
})
export class AddEDocumentFormComponent implements OnInit {
  formGroup: FormGroup;

  requiredFileTypes$ = new BehaviorSubject<DictionaryItem[]>([]);

  documentTypes$ = this.dictionaryService.getEDocumentTypes();
  documentParts$: Observable<DictionaryItem[]>;
  isRequiresExpirationDate$: Observable<boolean>;
  companies$ = this.dictionaryService.getCompaniesList();
  isSigned: boolean;

  private subs = new SubSink();
  private readonly peselValidators = [Validators.required, Validators.minLength(11), Validators.maxLength(11), Validators.pattern('^[0-9]{11}$'), peselValidator];

  constructor(
    private formBuilder: FormBuilder,
    private spinner: NgxSpinnerService,
    private dialog: MatDialog,
    private dialogRef: MatDialogRef<AbsenceModalComponent>,
    private snackbar: SnackBarService,
    private dictionaryService: DictionaryService,
    private eDocumentationService: EDocumentationService,
    private translateService: TranslateService,
    private workerService: WorkerService,
    @Inject(MAT_DIALOG_DATA) public data: AddEDocumentFormData,
  ) {
  }

  ngOnInit(): void {
    this.buildFormGroup();
    this.onDocumentTypeChangeHandlerInit();
    this.patchFormGroupValue();
    this.onPeselChangeHandlerInit()


    this.commentsControl.valueChanges.subscribe(a => {

      console.log(this.formGroup);
    })
  }

  get companyIdControl(): AbstractControl {
    return this.formGroup.get('companyId');
  }

  get companyControl(): AbstractControl {
    return this.formGroup.get('company');
  }
  get lastNameControl(): AbstractControl {
    return this.formGroup.get('lastName');
  }
  get firstNameControl(): AbstractControl {
    return this.formGroup.get('firstName');
  }
  get peselControl(): AbstractControl {
    return this.formGroup.get('pesel');
  }
  get commentsControl(): AbstractControl {
    return this.formGroup.get('comments');
  }
  get documentTypeIdControl(): AbstractControl {
    return this.formGroup.get('documentTypeId');
  }
  get documentPartIdControl(): AbstractControl {
    return this.formGroup.get('documentPartId');
  }
  get validUntilDateControl(): AbstractControl {
    return this.formGroup.get('validUntilDate');
  }
  get documentNameControl(): AbstractControl {
    return this.formGroup.get('documentName');
  }

  ngAfterViewInit(): void {

  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  close = () => this.dialogRef.close(false);

  requiredFileTypesLength(): number {
    return this.requiredFileTypes$.getValue().length;
  }

  async save() {
    const request: EFolderGridItemDto = {
      ...this.data.dto,
      CompanyId: this.companyIdControl.value,
      FirstName: this.firstNameControl.value,
      LastName: this.lastNameControl.value,
      Pesel: this.peselControl.value,
      DocumentPartId: this.documentPartIdControl.value,
      DocumentTypeId: this.documentTypeIdControl.value,
      ValidUntilDate: (this.validUntilDateControl.value as Moment)?.toDate(),
      DocumentName: this.documentNameControl.value,
      Comments: this.commentsControl.value,
      FileName: this.fileName,
      OriginalFileName: this.originalFileName,
      IsSigned: this.isSigned,
      StatusId: this.data.statusId
    }

    this.spinner.show();
    if (!this.data.dto) {
      await firstValueFrom(this.eDocumentationService.createEDocument(request)
        .pipe(tap((result) => this.closeDialogSuccessfully(this.translateService.instant(Messages.SuccessfullyCreateEDocument, { documentId: result.DocumentId }))))
        .pipe(finalize(() => this.spinner.hide())));
    } else {
      await firstValueFrom(this.eDocumentationService.updateEDocument(request)
        .pipe(tap((_) => this.closeDialogSuccessfully(Messages.SuccesfullyRejectedAbsence)))
        .pipe(finalize(() => this.spinner.hide())));
    }
  }

  sign() {
    this.dialog
      .open(SignModalComponent, { panelClass: 'sign-dialog' })
      .afterClosed()
      .pipe(first())
      .subscribe(async (isCreated: boolean) => {

        if (isCreated) {
          this.isSigned = true;

        }
      });
  }

  fileName: string;
  public originalFileName: string;

  async onFilesChange(files: File[]) {

    if (files.length) {
      await getBase64(files[0], async base64 =>
        await firstValueFrom(this.eDocumentationService
          .saveFile(base64)
          .pipe(tap(response => {
            this.fileName = response.FileName;
            this.originalFileName = files[0].name;
          }))
          .pipe(
            first(),
            finalize(() => this.spinner.hide()),
          )
        ))
    } else {

    }
  }

  private closeDialogSuccessfully = (message: string) => {
    this.snackbar.openSuccessSnackBar(message);
    this.dialogRef.close(true);
  };

  private buildFormGroup() {
    this.formGroup = this.formBuilder.group({
      companyId: [null, Validators.required],
      company: [null],
      lastName: [{ value: null, disabled: true }, Validators.required],
      firstName: [{ value: null, disabled: true }, Validators.required],
      pesel: [null, this.peselValidators],
      documentName: [null, Validators.required],
      comments: [null],
      documentTypeId: [null, [Validators.required]],
      documentPartId: [null, [Validators.required]],
      validUntilDate: [null]
    });
  }

  private patchFormGroupValue() {
    if (this.data.dto) {
      this.formGroup.patchValue({
        companyId: this.data.dto.CompanyId,
        lastName: this.data.dto.LastName,
        firstName: this.data.dto.FirstName,
        pesel: this.data.dto.Pesel,
        documentName: this.data.dto.DocumentName,
        comments: this.data.dto.Comments,
        documentTypeId: this.data.dto.DocumentTypeId,
        documentPartId: this.data.dto.DocumentPartId,
        validUntilDate: moment(this.data.dto.ValidUntilDate)
      });

      this.fileName = this.data.dto.FileName;
      this.originalFileName = this.data.dto.OriginalFileName;
      this.isSigned = this.data.dto.IsSigned;
    }

    if (this.data.isReadOnly) {
      this.formGroup.disable();
    }
  }

  private onDocumentTypeChangeHandlerInit() {
    this.documentParts$ = this.documentTypeIdControl.valueChanges
      .pipe(startWith(this.data.dto?.DocumentTypeId))
      .pipe(switchMap(id => this.dictionaryService.getEDocumentParts(id)))
      .pipe(tap(parts => {
        this.documentPartIdControl.reset();
        if (parts.length === 1) {
          this.documentPartIdControl.setValue(parts[0].Id);
        }
      }));

    this.isRequiresExpirationDate$ = combineLatest([this.documentTypeIdControl.valueChanges, this.documentTypes$])
      .pipe(map(([documentTypeId, documentTypes]) => documentTypes.find(dt => dt.Id === documentTypeId)?.IsRequiresExpirationDate))
      .pipe(tap(isRequiresExpirationDate => {
        if (!isRequiresExpirationDate) {
          this.validUntilDateControl.setValidators(undefined);
          this.validUntilDateControl.reset();
        } else {
          this.validUntilDateControl.setValidators(Validators.required);
        }
      }));
  }

  private onPeselChangeHandlerInit() {
    this.subs.sink = this.peselControl.valueChanges
      .pipe(tap(_ => {
        this.firstNameControl.reset();
        this.lastNameControl.reset();
      }))
      .pipe(filter(_ => this.peselControl.valid))
      .pipe(switchMap(pesel => this.workerService.getExistingExternalWorker({ Pesel: pesel } as GetExistingExternalWorkerRequest)))
      .subscribe(worker => {
        this.firstNameControl.setValue(worker.FirstName);
        this.lastNameControl.setValue(worker.LastName);
      });
  }
}